import React, { useState } from 'react';
import { formatDate, getParams, isAdmin } from '../data/utils';
import { AiOutlineEdit, AiOutlineEye, AiOutlinePlus, AiOutlineUser } from 'react-icons/ai';
import Header from '../components/Header';
import Modal from '../components/Modal';
import Dropdown from '../components/Dropdown';
import DataGrid from '../components/DataGrid';
import Pagination from '../components/Pagination';
import { ActionButton } from '../components/Button';
import { mockUsers } from '../data/mockData';
import { useFetch } from '../hooks/useFetch';
import { AllSenegalDepartments } from '../data/dummy';
import { usePatch } from '../hooks/usePatch';

const Users = () => {
	const [selectedUser, setSelectedUser] = useState(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [modalType, setModalType] = useState('create'); // 'create' or 'edit'
	const [params, setParams] = useState({
		page: 1,
		limit: 25,
		role: '',
		sortBy: 'name'
	});

	const { data: users, loading, totalItems, totalPages } = useFetch('/users', params, mockUsers);
	const { putRequest, loading: pacthLoading } = usePatch();

	const handleOpenModal = (user, type = 'create') => {
		setSelectedUser(user);
		setIsModalOpen(true);
		setModalType(type);
	};

	const handleParamsChange = (key, value) => {
		 setParams(getParams(key, value, params));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		const form = e.target;
		const formData = new FormData(form);
		const selectedDepts = Array.from(
			form.querySelector('select[name="depts"]').selectedOptions,
			option => option.value
		);

		const userData = Object.fromEntries(formData.entries());
		userData.depts = selectedDepts;
		userData.disabled = userData.disabled === 'false' ? false : true;
		if (!userData.email) {
			userData.email = selectedUser.email;
		}

		await putRequest(`/users/create-or-update-dashboard-user`, userData).then(() => {
			setIsModalOpen(false);
			setParams({ ...params });
		});
	};


	const onSearch = (searchString) => {
		setParams(prev => ({ ...prev, search: searchString, page: 1 }));
	};

	const getRoleBadge = (role) => {
		const roleMap = {
			'USER': { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Utilisateur' },
			'MODERATOR': { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Modérateur' },
			'ADMIN': { bg: 'bg-red-100', text: 'text-red-800', label: 'Administrateur' },
			'AGENT': { bg: 'bg-green-100', text: 'text-green-800', label: 'Agent' }
		};
		const config = roleMap[role] || roleMap['USER'];
		return (
			<span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${config.bg} ${config.text}`}>
				{config.label}
			</span>
		);
	};

	const getStatusBadge = (disabled) => {
		if (disabled === false) {
			return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Actif</span>;
		} else {
			return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Inactif</span>;
		}
	}
	// Templates pour le DataGrid
	const RoleTemplate = (props) => getRoleBadge(props.role);
	const EmailTemplate = (props) => <span className="text-sm text-gray-600">{props.email}</span>;
	const JoinedTemplate = (props) => <span className="text-sm">{formatDate(props.createdAt)}</span>;
	const StatusTemplate = (props) => getStatusBadge(props.disabled);
	const UserActionTemplate = (props) => (
		<div className='flex gap-2 justify-center'>
			<ActionButton
				onClick={() => handleOpenModal(props, 'edit')}
				icon={AiOutlineEdit}
				className='text-blue-600 border-blue-500 hover:bg-blue-500 hover:text-white'
			/>
		</div>
	);

	// Options pour les dropdowns
	const sortOptions = [
		{ label: 'Nom', value: 'name' },
		{ label: 'Rôle', value: 'role' },
	];

	const roleOptions = [
		{ label: 'Agent', value: 'AGENT' },
		{ label: 'Modérateur', value: 'MODERATOR' },
		{ label: 'Administrateur', value: 'ADMIN' },
	];



	// Colonnes pour le DataGrid
	const usersColumns = [
		{ field: 'fullName', headerText: 'Nom', width: 200, textAlign: 'Center' },
		{ headerText: 'Email', width: 250, textAlign: 'Center', template: EmailTemplate, allowEditing: false },
		{ headerText: 'Rôle', width: 150, textAlign: 'Center', template: RoleTemplate, allowEditing: false },
		{ headerText: 'Statut', width: 120, textAlign: 'Center', template: StatusTemplate, allowEditing: false },
		{ headerText: 'Date', width: 120, textAlign: 'Center', template: JoinedTemplate, allowEditing: false },
	];

	return (
		<div className='md:m-10 mt-auto p-2 pb-4 md:p-10 bg-gray-200 md:rounded-3xl rounded-xl w-100'>
			<Header category='Utilisateurs' title='' refresh={() => setParams({ ...params })} />

			<div className='grid grid-cols-1 md:grid-cols-4 gap-4 mb-4'>

				{
					isAdmin() && (
						<>
							<Dropdown
								label='Trier par'
								options={sortOptions}
								value={params.sortBy}
								onChange={(val) => handleParamsChange('sortBy', val)}
							/>
							<Dropdown
								label='Rôle'
								options={roleOptions}
								value={params.role}
								onChange={(val) => handleParamsChange('role', val)}
							/>
							<div className="col-span-2 flex justify-end">
								<button
									onClick={handleOpenModal}
									className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
								>
									<AiOutlinePlus />
									Nouveau utilisateur
								</button>
							</div>

						</>
					)
				}

			</div>

			<DataGrid
				data={users}
				columns={usersColumns}
				allowPaging={false}
				loading={loading}
				editing={{ allowDeleting: false, allowEditing: false }}
				onSearch={onSearch}
				toolbarOptions={[]}
				actionButtons={UserActionTemplate}
				extraComponents={
					<Pagination
						currentPage={params.page}
						totalPages={totalPages}
						totalItems={totalItems}
						onPageChange={(val) => handleParamsChange('page', val)}
					/>
				}
			/>

			{isModalOpen && selectedUser && (
				<Modal show={isModalOpen} title={modalType === 'create' ? 'Nouvel utilisateur' : 'Modifier l\'utilisateur'} onClose={() => setIsModalOpen(false)} size="md">
					<div className="space-y-4">
						<form onSubmit={handleSubmit} className="space-y-4">
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Nom complet</label>
								<input name='fullName' defaultValue={selectedUser.fullName} required type="text" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2" placeholder="Entrez le nom complet" />
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
								<input name='email' defaultValue={selectedUser.email} disabled={modalType === 'edit'} required type="email" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2" placeholder="Entrez l'email" />
							</div>
							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Numero</label>
								<input name='phoneNumber' defaultValue={selectedUser.phoneNumber} required type="text" className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2" placeholder="Entrez le numero de telephone" />
							</div>
							{
								isAdmin() && (
									<>
										<div>
											<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Rôle</label>
											<select name='role' required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2">
												{
													[
														{ label: 'Agent', value: 'AGENT', selected: selectedUser?.role === 'AGENT' },
														{ label: 'Modérateur', value: 'MODERATOR', selected: selectedUser?.role === 'MODERATOR' },
														{ label: 'Administrateur', value: 'ADMIN', selected: selectedUser?.role === 'ADMIN' },
													].map((role) => (
														<option key={role.value} selected={role.selected} value={role.value}>{role.label}</option>
													))
												}

											</select>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Departements</label>
											<select name='depts' required multiple className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2">
												{
													AllSenegalDepartments.map((dept) => (
														<option key={dept} selected={selectedUser?.depts?.includes(dept)} value={dept}>{dept}</option>
													))
												}
											</select>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
												Desactiver
											</label>
											<select name='disabled' required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2">
												<option value={false} selected={selectedUser?.disabled === false}>Non</option>
												<option value={true} selected={selectedUser?.disabled === true}>Oui</option>
											</select>
										</div>
									</>
								)
							}



							<div>
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
									Mot de passe
								</label>
								<input
									type="password"
									name="password"
									pattern="(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,}"
									required={modalType === 'create'}
									className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
									placeholder="Entrez le mot de passe"
								/>
								<small className="text-gray-500">
									Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial.
								</small>
							</div>

							<div className="flex justify-end pt-4">
								<button disabled={pacthLoading} type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 mr-2">
									Annuler
								</button>
								<button disabled={pacthLoading} type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
									{modalType === 'create' ? 'Créer' : 'Enregistrer'}
									{pacthLoading && <span className="ml-2 loader"></span>}
								</button>
							</div>
						</form>
					</div>
				</Modal>
			)}
		</div>
	);
};

export default Users;
